import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import db from "../../services/query";
import lbl from "../../data/Labels.json";
import { PopUpContext } from "../../contexts/popUpContext";
import ParityDialog from "../common/ParityDialog";
import "../common/parity.css";
import "./studentToClass.css";

function studentLabel(row) {
  if (!row) return "";
  const name = [row.student_name, row.student_lastname].filter(Boolean).join(" ").trim();
  return name || row.full_name || row.name || `محصل #${row.student_id}`;
}

function StudentToClass() {
  const { togglePopUp, popUp } = useContext(PopUpContext);
  const classLocked = Boolean(popUp?.classLocked || popUp?.classId);
  const lockedClassId = popUp?.classId ? Number(popUp.classId) : null;
  const studentFixed = popUp?.student && !classLocked ? popUp.student : null;
  const isEdit = Boolean(popUp?.edit);
  const isDelete = Boolean(popUp?.delete);

  const [faculties, setFaculties] = useState([]);
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [facultyId, setFacultyId] = useState("");
  const [classId, setClassId] = useState(lockedClassId ? String(lockedClassId) : "");
  const [groupId, setGroupId] = useState("");
  const [studentId, setStudentId] = useState(studentFixed?.student_id ? String(studentFixed.student_id) : "");
  const [studentName, setStudentName] = useState(studentFixed ? studentLabel(studentFixed) : "");
  const [classLabel, setClassLabel] = useState(popUp?.classDesc || "");
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadError, setLoadError] = useState("");
  const entryYear = popUp?.entryYear;

  const title = isEdit
    ? lbl.EditStudentToClass
    : isDelete
      ? lbl.DeleteStudentToClass
      : lbl.AddStudentToClass;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (classLocked && lockedClassId) {
          const { data: body } = await db.getAll(`classes/${lockedClassId}`);
          const info = body?.data?.[0] || body?.data || {};
          if (cancelled) return;
          setClassLabel(info.class_desc || popUp?.classDesc || `صنف ${lockedClassId}`);
          setFacultyId(info.faculty_id ? String(info.faculty_id) : "");
          setClassId(String(lockedClassId));
          const { data: gBody } = await db.getAll(`dropdowns/groups?classId=${lockedClassId}`);
          if (!cancelled) setGroups(gBody?.data || []);
          return;
        }

        const seedFaculty =
          studentFixed?.faculty_id ||
          studentFixed?.selected_faculty_id ||
          studentFixed?.faculty ||
          "";
        const { data: fBody } = await db.getAll("dropdowns/faculties");
        if (cancelled) return;
        setFaculties(fBody?.data || []);
        if (seedFaculty) {
          setFacultyId(String(seedFaculty));
        }
        if (studentFixed?.class_id) setClassId(String(studentFixed.class_id));
        if (studentFixed?.group_id) setGroupId(String(studentFixed.group_id));
      } catch (_) {
        if (!cancelled) setLoadError("بارگیری معلومات صنف ناموفق بود.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (classLocked || !facultyId || !entryYear) return;
    let cancelled = false;
    db.getAll(`dropdowns/classes?entryYearId=${entryYear}&facultyId=${facultyId}`)
      .then(({ data: body }) => {
        if (cancelled) return;
        const rows = body?.data || [];
        setClasses(rows);
        if (classId && !rows.some((c) => String(c.id) === String(classId))) {
          setClassId("");
          setGroupId("");
          setGroups([]);
        }
      })
      .catch(() => {
        if (!cancelled) setClasses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [facultyId, entryYear, classLocked]);

  useEffect(() => {
    if (classLocked || !classId) return;
    let cancelled = false;
    db.getAll(`dropdowns/groups?classId=${classId}`)
      .then(({ data: body }) => {
        if (!cancelled) setGroups(body?.data || []);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [classId, classLocked]);

  useEffect(() => {
    if (!classLocked || isDelete || isEdit) return;
    const term = search.trim();
    if (term.length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "12",
          orderBy: "student_id",
          order: "desc",
          search: term,
        });
        if (entryYear) params.set("entryYearId", String(entryYear));
        if (facultyId) params.set("facultyId", String(facultyId));
        const { data: body } = await db.getAll(`students/applicants?${params}`);
        if (!cancelled) setHits(body?.data || []);
      } catch (_) {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search, classLocked, entryYear, facultyId, isDelete, isEdit]);

  const selectedClassName = useMemo(() => {
    if (classLabel) return classLabel;
    const row = classes.find((c) => String(c.id) === String(classId));
    return row?.name || row?.class_desc || "";
  }, [classLabel, classes, classId]);

  const doneRef = useRef(false);

  const close = () => {
    if (doneRef.current) return;
    togglePopUp({});
  };

  const onSubmit = async () => {
    if (isDelete) {
      await db.delete(`students/class/${popUp.student.student_id}`, popUp.student.class_id);
    } else if (isEdit) {
      await db.edit(`students/class/${popUp.student.student_id}/${popUp.student.class_id}`, {
        student: Number(studentId),
        class: Number(classId),
        group: groupId ? Number(groupId) : null,
      });
    } else {
      if (!studentId || !classId) throw new Error("محصل و صنف لازم است.");
      await db.save("students/class", {
        student: Number(studentId),
        class: Number(classId),
        group: groupId ? Number(groupId) : null,
      });
    }
    if (typeof popUp?.callback === "function") {
      try {
        popUp.callback({ status: 200 });
      } catch (_) {}
    }
    doneRef.current = true;
    togglePopUp({ form: "toast", message: isDelete ? "حذف شد" : "ثبت شد" });
  };

  const pickHit = (row) => {
    setStudentId(String(row.student_id));
    setStudentName(studentLabel(row));
    setSearch("");
    setHits([]);
  };

  return (
    <ParityDialog
      title={title}
      className="student-to-class-dialog"
      onClose={close}
      onSubmit={onSubmit}
      submitLabel={isDelete ? lbl.Delete : lbl.Save}
      disabled={Boolean(loadError)}
      submitDisabled={!isDelete && (!studentId || !classId)}
    >
      {loadError ? <p role="alert">{loadError}</p> : null}

      {classLocked ? (
        <div className="stc-banner" style={{ gridColumn: "1 / -1" }}>
          <span>صنف</span>
          <strong>{selectedClassName || `شماره ${classId}`}</strong>
        </div>
      ) : null}

      {studentFixed ? (
        <div className="stc-banner" style={{ gridColumn: "1 / -1" }}>
          <span>{lbl.Student}</span>
          <strong>{studentName}</strong>
          {studentFixed.aria_id ? <em>{studentFixed.aria_id}</em> : null}
        </div>
      ) : null}

      {classLocked && !studentFixed && !isDelete ? (
        <label className="stc-search" style={{ gridColumn: "1 / -1" }}>
          جستجوی محصل / داوطلب
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="نام، کد دانشجو، یا شماره تماس"
            autoComplete="off"
          />
          {studentId ? (
            <div className="stc-picked">
              انتخاب‌شده: <strong>{studentName}</strong>
              <button type="button" className="mis-button is-secondary" onClick={() => { setStudentId(""); setStudentName(""); }}>
                حذف انتخاب
              </button>
            </div>
          ) : null}
          {searching ? <small>در حال جستجو…</small> : null}
          {!searching && search.trim().length >= 2 && !hits.length ? <small>نتیجه‌ای یافت نشد.</small> : null}
          {hits.length ? (
            <ul className="stc-hits">
              {hits.map((row) => (
                <li key={row.student_id}>
                  <button type="button" onClick={() => pickHit(row)}>
                    <strong>{studentLabel(row)}</strong>
                    <span>{row.aria_id || ""}</span>
                    <span>{row.department || row.faculty || ""}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </label>
      ) : null}

      {!classLocked && !isDelete ? (
        <label>
          پوهنحی
          <select
            value={facultyId}
            disabled={isEdit}
            onChange={(e) => {
              setFacultyId(e.target.value);
              setClassId("");
              setGroupId("");
              setGroups([]);
            }}
            required
          >
            <option value="">انتخاب</option>
            {faculties.map((f) => (
              <option key={f.id || f.faculty_id} value={f.id || f.faculty_id}>
                {f.name || f.faculty_name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {!classLocked && !isDelete ? (
        <label>
          صنف
          <select
            value={classId}
            disabled={isEdit || !facultyId}
            onChange={(e) => {
              setClassId(e.target.value);
              setGroupId("");
            }}
            required
          >
            <option value="">انتخاب</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.class_desc}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {!isDelete ? (
        <label>
          گروپ <span className="stc-optional">(اختیاری)</span>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={!classId}>
            <option value="">بدون گروپ</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name || g.group_name || g.id}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p>
          حذف «{studentName}» از صنف {selectedClassName || popUp?.student?.class_desc || ""}؟
        </p>
      )}
    </ParityDialog>
  );
}

export default StudentToClass;
